import type { CheckInBody, CheckOutBody, Position } from "./employee-api";

/**
 * The bodies the employee portal punches with.
 *
 * Check-in and check-out are deliberately different shapes: only check-in
 * records the browser in `deviceInfo`, and the API refuses any property it
 * does not declare (`forbidNonWhitelisted`). Built here, once, so the two can
 * never be shared again — reusing one object is what made every web check-out
 * fail with "property deviceInfo should not exist" (see punch.test.ts).
 */

/**
 * A browser that cannot place the user may report `Infinity`. Sending that
 * fails validation outright; a large finite number fails the accuracy
 * threshold instead, which is the honest outcome and leaves a record.
 */
const USELESS_FIX_M = 100_000;

function fix(position: Position) {
  return {
    latitude: position.latitude,
    longitude: position.longitude,
    accuracy: Number.isFinite(position.accuracy) ? position.accuracy : USELESS_FIX_M,
    timestamp: new Date().toISOString(),
  };
}

/** Check-in, and the preview that is judged on the same body. */
export function checkInBody(position: Position, deviceInfo: string | undefined): CheckInBody {
  return { ...fix(position), ...(deviceInfo ? { deviceInfo } : {}) };
}

export function checkOutBody(position: Position): CheckOutBody {
  return fix(position);
}

/**
 * How old a reading may be and still be sent with a punch (FEAT-2026-025).
 *
 * The confirmation can sit open while the employee reads it — or walks away.
 * A punch records where they are when it is sent, so past this age the portal
 * takes a new reading first. Thirty seconds, as in the app (mobile BR-ATT-009),
 * where a reading hours old once passed a strict geofence from home.
 */
export const PUNCH_READING_MAX_AGE_MS = 30_000;

/**
 * Whether a reading taken at `atMs` may still be sent at `nowMs`. One stamped
 * well ahead of the clock counts as stale: the clock has moved, so its age
 * cannot be known.
 */
export function isReadingFresh(atMs: number, nowMs: number): boolean {
  return Math.abs(nowMs - atMs) <= PUNCH_READING_MAX_AGE_MS;
}
